import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "../lib/api";
import { fmtMoney } from "../components/ops/opsUtils";
import { Building2, ArrowLeft, UserPlus, Edit3, Save, Trash2, ExternalLink, Loader2, Link2, Copy, Check, Eye, FileText, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function FirmProfile() {
  const { id } = useParams();
  const [firm, setFirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", domains: "", primary_contact_name: "", primary_contact_email: "", primary_contact_phone: "", notes: "" });
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", role: "attorney" });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/operations/firms/${id}`);
      setFirm(data);
      setForm({
        name: data.name, address: data.address, domains: (data.domains || []).join(", "),
        primary_contact_name: data.primary_contact_name || "", primary_contact_email: data.primary_contact_email || "",
        primary_contact_phone: data.primary_contact_phone || "", notes: data.notes || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const save = async () => {
    try {
      await api.patch(`/operations/firms/${id}`, {
        ...form,
        domains: form.domains.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Saved");
      setEdit(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addUser = async () => {
    try {
      await api.post(`/operations/firms/${id}/users`, { firm_id: id, ...newUser });
      toast.success("User added");
      setShowAddUser(false);
      setNewUser({ email: "", name: "", role: "attorney" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeUser = async (userId) => {
    if (!window.confirm("Remove this user from the firm?")) return;
    try {
      await api.delete(`/operations/firms/${id}/users/${userId}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [issuingFor, setIssuingFor] = useState(null);
  const [issuedLink, setIssuedLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const issueLink = async (user) => {
    setIssuingFor(user.user_id);
    try {
      const { data } = await api.post(`/operations/firms/${id}/portal-tokens`, {
        user_email: user.email, expires_days: 90,
      });
      const url = `${window.location.origin}/portal/r/${data.token}`;
      setIssuedLink({ user, url, expires_at: data.expires_at });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setIssuingFor(null); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(issuedLink.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed — select the URL manually."); }
  };

  const viewAsFirm = async () => {
    window.open(`/portal/impersonate#firm=${encodeURIComponent(id)}`, "_blank");
  };
  const viewAsUser = async (user) => {
    window.open(`/portal/impersonate#firm=${encodeURIComponent(id)}&user=${encodeURIComponent(user.email)}`, "_blank");
  };

  // Phase 1: download the firm portfolio statement PDF (uses the exact same
  // compute_loan_payoff_v2 engine as contracts + payoff letters)
  const [generatingStmt, setGeneratingStmt] = useState(false);
  const [includeSettled, setIncludeSettled] = useState(false);
  const [emailingStmt, setEmailingStmt] = useState(false);

  const downloadStatement = async () => {
    setGeneratingStmt(true);
    try {
      // Record the JSON snapshot first (history) — backend creates a row in firm_statements
      await api.post(`/operations/firms/${id}/statement`, { include_settled: includeSettled });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/operations/firms/${id}/statement/pdf?include_settled=${includeSettled}`;
      // Append auth token so the new tab can authorize the GET — admin Bearer
      const t = JSON.parse(localStorage.getItem("instabridge_admin"))?.access_token;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) throw new Error(`PDF returned ${res.status}`);
      const blob = await res.blob();
      const fname = `${(firm?.name || "firm").replace(/\s+/g, "_")}_portfolio_${new Date().toISOString().slice(0, 10)}.pdf`;
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success("Statement downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGeneratingStmt(false); }
  };

  const emailStatement = async () => {
    if (!window.confirm(`Email statement to ${firm?.primary_contact_email || "the firm's primary contact"}?`)) return;
    setEmailingStmt(true);
    try {
      const { data } = await api.post(`/operations/firms/${id}/statement/email`, { include_settled: includeSettled });
      if (data.sent) toast.success(`Sent to ${data.to}`);
      else toast.error(`Could not send: ${data.error || "unknown error"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEmailingStmt(false); }
  };

  if (loading) return <div className="p-10 text-[#6B7280]" data-testid="firm-loading">Loading firm…</div>;
  if (!firm) return null;

  return (
    <div className="p-6 md:p-8 max-w-[1300px] mx-auto" data-testid="firm-profile">
      <Link to="/admin/firms" className="inline-flex items-center text-sm text-[#6B7280] hover:text-[#0A2540] mb-3" data-testid="back-to-firms">
        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> All firms
      </Link>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-2">
              <Building2 className="w-3.5 h-3.5" /> Law firm
            </div>
            {edit ? (
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="text-2xl font-display font-semibold" data-testid="firm-name-input" />
            ) : (
              <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">{firm.name}</h1>
            )}
            <div className="text-sm text-[#6B7280] mt-1">{(firm.domains || []).join(", ") || "no domains"}</div>
          </div>
          {edit ? (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => { setEdit(false); load(); }} data-testid="cancel-edit">Cancel</Button>
              <Button onClick={save} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="save-firm"><Save className="w-3.5 h-3.5 mr-1.5" /> Save</Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => viewAsFirm()} data-testid="impersonate-firm" className="text-[#A16207] border-[#FDE68A] hover:bg-[#FEF9C3]"><Eye className="w-3.5 h-3.5 mr-1.5" /> View portal</Button>
              <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-firm"><Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-4 gap-5 pt-4 border-t border-[#F3F4F6]">
          <Stat label="Users" value={firm.users?.length || 0} />
          <Stat label="Active loans" value={firm.active_loan_count} accent />
          <Stat label="Total loans" value={firm.loan_count} />
          <Stat label="Principal" value={fmtMoney(firm.total_principal)} />
        </div>
        {edit && (
          <div className="grid sm:grid-cols-2 gap-3 mt-5 pt-5 border-t border-[#F3F4F6]">
            <Field label="Address"><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Domains (comma-separated)"><Input value={form.domains} onChange={(e) => setForm({ ...form, domains: e.target.value })} /></Field>
            <Field label="Primary contact name"><Input value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} /></Field>
            <Field label="Primary contact email"><Input value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} /></Field>
            <Field label="Primary contact phone"><Input value={form.primary_contact_phone} onChange={(e) => setForm({ ...form, primary_contact_phone: e.target.value })} /></Field>
            <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
        )}
      </div>

      {/* Portfolio statement card */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-5" data-testid="statement-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] text-[#0066FF] flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
                Portfolio statement
              </div>
              <div className="font-medium text-[#0A2540]">Premium PDF · live balances · multi-advance grouping · aging buckets</div>
              <div className="text-xs text-[#6B7280] mt-0.5">Uses the same engine as funding agreements and payoff letters (per-funding rate, 6-month compounding, max-ownership cap).</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-[#6B7280]">
              <input type="checkbox" checked={includeSettled} onChange={(e) => setIncludeSettled(e.target.checked)} data-testid="include-settled-chk" />
              Include settled
            </label>
            <Button variant="outline" onClick={emailStatement} disabled={emailingStmt || !firm.primary_contact_email} data-testid="email-statement-btn" title={!firm.primary_contact_email ? "Set primary_contact_email on the firm first" : "Email to primary contact"}>
              {emailingStmt ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
              Email statement
            </Button>
            <Button onClick={downloadStatement} disabled={generatingStmt} className="bg-[#0A2540] hover:bg-[#001A3D]" data-testid="download-statement-btn">
              {generatingStmt ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
              Generate PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-[#E5E7EB] rounded-lg">
          <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
            <div className="font-medium text-[#0A2540]">Users ({firm.users?.length || 0})</div>
            <Button size="sm" onClick={() => setShowAddUser(true)} className="bg-[#0066FF] hover:bg-[#0052CC] h-8" data-testid="add-user-btn"><UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add user</Button>
          </div>
          {showAddUser && (
            <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB] grid sm:grid-cols-3 gap-2">
              <Input placeholder="email@firm.com" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" />
              <Input placeholder="Name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" />
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attorney">Attorney</SelectItem>
                  <SelectItem value="paralegal">Paralegal</SelectItem>
                  <SelectItem value="case_manager">Case Manager</SelectItem>
                  <SelectItem value="firm_admin">Firm Admin</SelectItem>
                </SelectContent>
              </Select>
              <div className="sm:col-span-3 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowAddUser(false)}>Cancel</Button>
                <Button size="sm" onClick={addUser} disabled={!newUser.email.trim()} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="create-user-btn">Add</Button>
              </div>
            </div>
          )}
          <ul className="divide-y divide-[#F3F4F6]">
            {(firm.users || []).map((u) => (
              <li key={u.user_id} className="px-5 py-3 flex items-center gap-3" data-testid={`user-${u.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0A2540]">{u.name || u.email}</div>
                  <div className="text-xs text-[#6B7280]">{u.email} · {u.role} · {u.status}</div>
                </div>
                <button onClick={() => viewAsUser(u)} className="inline-flex items-center gap-1 text-xs text-[#A16207] hover:text-[#92400E]" data-testid={`view-as-user-${u.user_id}`} title="Open the portal as this user">
                  <Eye className="w-3.5 h-3.5" /> View as
                </button>
                <button onClick={() => issueLink(u)} disabled={issuingFor === u.user_id} className="inline-flex items-center gap-1 text-xs text-[#0066FF] hover:text-[#0052CC] disabled:opacity-50" data-testid={`issue-link-${u.user_id}`}>
                  {issuingFor === u.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                  Portal link
                </button>
                <button onClick={() => removeUser(u.user_id)} className="text-[#9CA3AF] hover:text-[#B91C1C]" data-testid={`remove-user-${u.user_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </li>
            ))}
            {(firm.users || []).length === 0 && <li className="px-5 py-8 text-center text-sm text-[#6B7280]">No users yet</li>}
          </ul>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg">
          <div className="px-5 py-3 border-b border-[#E5E7EB]">
            <div className="font-medium text-[#0A2540]">Loans ({firm.loans?.length || 0})</div>
          </div>
          <ul className="divide-y divide-[#F3F4F6] max-h-[500px] overflow-y-auto">
            {(firm.loans || []).slice(0, 50).map((ln) => (
              <li key={ln.loan_id} className="px-5 py-2.5 flex items-center justify-between hover:bg-[#F9FAFB]">
                <div className="min-w-0">
                  <Link to={`/admin/loans/${ln.loan_id}`} className="text-sm font-medium text-[#0066FF] hover:underline">{ln.client_name}</Link>
                  <div className="text-xs text-[#6B7280]">{ln.loan_id} · {ln.current_status} · {formatDate(ln.funding_date)}</div>
                </div>
                <div className="text-sm tabular-nums text-[#0A2540]">{fmtMoney(ln.principal)}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {issuedLink && (
        <div className="fixed inset-0 z-50 bg-[#0A2540]/60 backdrop-blur-sm flex items-center justify-center p-5" data-testid="issued-link-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-7">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-2">
              <Link2 className="w-3.5 h-3.5" /> Bookmarkable portal link
            </div>
            <h2 className="font-display text-2xl font-semibold text-[#0A2540]">Share with {issuedLink.user.name || issuedLink.user.email}</h2>
            <p className="text-sm text-[#6B7280] mt-1">No password required. Anyone with this link can view your firm's cases until {new Date(issuedLink.expires_at).toLocaleDateString()}.</p>
            <div className="mt-5 flex items-center gap-2 p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
              <input readOnly value={issuedLink.url} className="flex-1 bg-transparent text-xs font-mono text-[#0A2540] outline-none" data-testid="issued-link-url" />
              <button onClick={copyLink} className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-[#0066FF] hover:bg-[#0052CC] text-white text-xs font-medium" data-testid="copy-link-btn">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setIssuedLink(null)} data-testid="close-link-modal">Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
      <div className={`tabular-nums ${accent ? "text-[#0066FF] font-display text-2xl font-semibold" : "text-[#0A2540] text-xl font-medium"}`}>{value}</div>
    </div>
  );
}
function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}
